import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import {
  C_LEVEL_FILTER,
  CRITICALITY_FILTER,
  TAG_FILTER,
} from '@/features/dashboard/api/schemas'

/**
 * The multi-select filters of the regulatory deep dive.
 * `filter` is the id a card lists in its stub's `filters`; `param` is both the URL
 * parameter holding the selection and the query parameter the card is fetched with.
 * Their values are fixed lists, as no Managerium endpoint lists them.
 *
 * @TODO Move these lists to Managerium, so they follow the data.
 */
export const REGULATORY_FILTERS = [
  {
    filter: C_LEVEL_FILTER,
    param: 'cLevel',
    label: 'C-Level',
    options: [
      'CEO',
      'CFO',
      'CMO',
      'Co-CEO',
      'COO',
      'CPBO',
      'CPeO',
      'CRO',
      'CTO',
    ],
    searchable: false,
  },
  {
    filter: CRITICALITY_FILTER,
    param: 'criticality',
    label: 'Criticality',
    options: ['1', '2', '3', '4'],
    searchable: false,
  },
  {
    filter: TAG_FILTER,
    param: 'tag',
    label: 'Tag',
    options: [
      'Deloitte AML/CFT External Audit Spanish Branch',
      'Deloitte AML/CFT External Audit Spanish Branch.',
      'Deloitte WpHG Audit 2025',
      'Deloitte Year End Audit 2024',
      'Deloitte Year End Audit 2024 (AML - AG)',
      'Deloitte Year End Audit 2024 (AML - Bank AG)',
      'Deloitte Year End Audit 2025',
      'Deloitte Year End Audit 2025 (AFC) - N26 Bank SE',
      'Deloitte Year End Audit 2025 (AFC) - N26 SE',
      'Finding Labelled Dora-Relevant',
      'PWC Year End 2019',
      'PWC Year End 2020',
      'PWC Year End 2021',
      'PWC Year End Audit 2022',
      'PWC Year End Audit 2023',
      'Sonderprüfung gemäß § 44 Abs. 1 Satz 2 KWG vom 16. August 2024',
      'Sonderprüfung gemäß § 44 Abs. 1 Satz 2 KWG vom 16. August 2024 (VISTA)',
      '§ 44 KWG (Bundesbank / BaFin)',
    ],
    searchable: true,
  },
] as const

export type RegulatoryFilter = (typeof REGULATORY_FILTERS)[number]
export type RegulatoryParam = RegulatoryFilter['param']

/** What is selected per filter, by its param: `{ cLevel: ['CEO'], criticality: [], tag: [] }`. */
export type RegulatorySelection = Record<RegulatoryParam, string[]>

export const readRegulatorySelection = (
  params: URLSearchParams
): RegulatorySelection =>
  Object.fromEntries(
    REGULATORY_FILTERS.map(({ param, options }) => {
      const requested = params.getAll(param)
      // In the filter's order, and only values it offers.
      return [param, options.filter(option => requested.includes(option))]
    })
  ) as RegulatorySelection

/** The regulatory selections, kept in the URL so a view can be shared. */
export const useRegulatoryFilters = () => {
  const [params, setParams] = useSearchParams()
  const selection = useMemo(() => readRegulatorySelection(params), [params])

  const toggle = useCallback(
    (param: RegulatoryParam, value: string) =>
      setParams(
        current => {
          const next = new URLSearchParams(current)
          const selected = current.getAll(param)
          next.delete(param)
          const kept = selected.includes(value)
            ? selected.filter(existing => existing !== value)
            : [...selected, value]
          kept.forEach(existing => next.append(param, existing))
          return next
        },
        { replace: true }
      ),
    [setParams]
  )
  return { selection, toggle }
}
